import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Briefcase, ExternalLink, Globe, Mail, Monitor } from 'lucide-react'
import { EXTERNAL_LINKS } from '../../constants/contact'
import { useLanguage } from '../../context/LanguageContext'
import { ensureEnglishForCareers } from '../../utils/careers'
import { useContactHref } from '../../hooks/useContactHref'

const TOP_LINKS = [
  {
    key: 'eSchool',
    href: EXTERNAL_LINKS.eSchool,
    icon: Monitor,
    external: true,
  },
  {
    key: 'jobApplication',
    href: '/careers',
    icon: Briefcase,
    external: false,
  },
  {
    key: 'contact',
    icon: Mail,
    external: false,
  },
]

function TopBarLink({ linkKey, href, icon: Icon, external }) {
  const { t } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const className =
    'inline-flex min-h-9 shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium text-white/85 transition-colors hover:bg-white/10 hover:text-white sm:gap-1.5 sm:px-2.5 sm:text-xs md:gap-2 md:px-3 md:text-sm'

  const content = (
    <>
      <Icon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
      <span className="hidden min-[360px]:inline">{t(`nav.${linkKey}`)}</span>
      {external && <ExternalLink className="hidden h-3 w-3 opacity-60 md:inline" aria-hidden />}
    </>
  )

  if (external) {
    return (
      <a key={linkKey} href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {content}
      </a>
    )
  }

  return (
    <Link
      key={linkKey}
      to={href}
      className={className}
      onClick={() => {
        if (href === '/careers') ensureEnglishForCareers(language, setLanguage)
      }}
    >
      {content}
    </Link>
  )
}

function LanguageToggle() {
  const { language, toggleLanguage } = useLanguage()
  const className =
    'inline-flex min-h-9 shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium text-white/85 transition-colors hover:bg-white/10 hover:text-white sm:gap-1.5 sm:px-2.5 sm:text-xs md:gap-2 md:px-3 md:text-sm'

  return (
    <button
      type="button"
      onClick={toggleLanguage}
      className={className}
    >
      <Globe className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
      <span>{language === 'en' ? 'AR' : 'EN'}</span>
    </button>
  )
}

export default function TopBar() {
  const contactHref = useContactHref()

  return (
    <div className="border-b border-white/10 bg-primary text-white">
      <div className="mx-auto flex max-w-7xl min-w-0 items-center justify-end gap-0.5 overflow-x-auto px-3 sm:gap-1 sm:px-6 lg:gap-2 lg:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TOP_LINKS.map(({ key, href, ...link }) => (
          <TopBarLink
            key={key}
            {...link}
            linkKey={key}
            href={key === 'contact' ? contactHref : href}
          />
        ))}
        <LanguageToggle />
      </div>
    </div>
  )
}
